import React, { useCallback, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { useFela } from 'react-fela';
import * as controlsActions from '../../actions/controls';
import {
  CHROMECAST_STATES,
  formatHhMmSs,
  formatTime,
} from '../utils';
import { Spacer } from '../common';
import { ControlBarContainer, ControlsContainer, Group, Time } from './styles';
import Fullscreen from './Fullscreen/Fullscreen';
import ProgressBarContainer from './ProgressBar/ProgressBarContainer';
import Play from './Play/Play';
import Rewind from './Seek/Rewind';
import FastForward from './Seek/FastForward';
import Settings from './Settings/Settings';
import Volume from './Volume/Volume';
import AirPlay from './AirPlay/AirPlay';
import { selectNormalizedCurrentTime, selectNormalizedInterval } from '../../selectors';
import LiveLabel from './styles/LiveLabel';
import { useOptions } from '../../OptionsContext';
import platform, { OS_TYPES } from '../../../player/utils/platform';
import { usePlayer } from '../../PlayerContext';

const progressBarContainerRules = ({ visible = true }) => ({
  visibility: visible ? 'visible' : 'hidden',
});

const ControlBar = React.forwardRef(({ onHoverStateChange }, ref) => {
  const {
    chromecastState,
    chromecastEnabled,
    airplayEnabled,
    isPlayingAds,
    isPlayingDaiAds,
    rights,
    playing,
    isLive,
    viewableMetadata,
    controlsVisible,
  } = useSelector(({ playback, controls }) => ({
    chromecastState: playback.chromecastState,
    chromecastEnabled: playback.chromecastEnabled,
    airplayEnabled: playback.airplayEnabled,
    isPlayingAds: playback.isPlayingAds,
    isPlayingDaiAds: playback.isPlayingDaiAds,
    rights: playback.rights,
    playing: playback.playing,
    isLive: playback.isLive,
    viewableMetadata: playback.viewableMetadata,
    controlsVisible: controls.controlsVisible,
  }), shallowEqual);

  const [isProgressBarVisible, setIsProgressBarVisible] = useState(true);
  const displayProgressBarTimeoutRef = useRef(null);

  const { seekStepInSeconds, hide } = useOptions();
  const dispatch = useDispatch();

  const isMobile = [OS_TYPES.IOS, OS_TYPES.ANDROID].includes(platform.os);

  const onMouseEnter = useCallback(() => onHoverStateChange(true), []);
  const onMouseLeave = useCallback(() => onHoverStateChange(false), []);
  const onMenuToggle = useCallback((value) => {
    setIsProgressBarVisible(value);
    dispatch(controlsActions.showMenu(!value));
  }, []);

  const isChromecasting = chromecastEnabled && chromecastState === CHROMECAST_STATES.CONNECTED;

  const player = usePlayer();
  const isBetweenAds = !isChromecasting && isPlayingAds && (
    player.adsPlayerElement?.readyState === HTMLMediaElement.HAVE_NOTHING || player.adsPlayerElement?.ended
  );

  const controlBarVisible = controlsVisible || isChromecasting || (!playing && !isBetweenAds);

  const isRewindVisible = !isPlayingAds && (!isPlayingDaiAds || rights.adsRewind) && !isMobile;
  const isFastForwardVisible = !isPlayingAds && (!isPlayingDaiAds || rights.adsFastForward) && !isMobile;
  const isPlayVisible = !isPlayingDaiAds || rights.adsPause || !playing;
  const isQualityVisible = !isPlayingAds && !hide.quality && !isMobile;

  const thickMode = isPlayingAds && !controlsVisible && playing;

  const [,, duration] = useSelector(selectNormalizedInterval, shallowEqual);
  const currentTime = useSelector(selectNormalizedCurrentTime);

  const timeUntilLive = duration - currentTime;
  const isPlayingLive = !isPlayingAds && viewableMetadata.isLiveEvent && isLive && playing && timeUntilLive < seekStepInSeconds;

  const { css } = useFela({ visible: isProgressBarVisible });

  return (
    <ControlBarContainer
      ref={ref}
      isPlayingAds={isPlayingAds}
      hidden={!controlBarVisible}
      {...isMobile ? {
        onTouchStart: onMouseEnter, onTouchEnd: onMouseLeave,
      } : {
        onMouseEnter, onMouseLeave,
      }}
    >
      {!isPlayingAds && (
        <div className={css(progressBarContainerRules)}>
          <ProgressBarContainer />
        </div>
      )}

      <ControlsContainer>
        <Group {...isMobile && { fontSize: 'small' }}>
          {!thickMode && !viewableMetadata.isLiveEvent && !isPlayingAds && (
            <>
              <Time data-cy="hp-current-time">
                {viewableMetadata.isLiveEvent && viewableMetadata.start
                  ? formatTime(viewableMetadata.start)
                  : formatHhMmSs(currentTime)}
              </Time>
              <Spacer width={5} />/<Spacer width={5} />
              <Time right data-cy="hp-remaining-time">
                {viewableMetadata.isLiveEvent && viewableMetadata.stop
                  ? formatTime(viewableMetadata.stop)
                  : formatHhMmSs(duration)}
              </Time>
            </>
          )}
          {viewableMetadata.isLiveEvent && !isPlayingAds && <LiveLabel isLive={isPlayingLive} timeUntilLive={timeUntilLive} />}
        </Group>

        <Group central>
          {isRewindVisible && <Rewind />}
          {isPlayVisible && <Play />}
          {isFastForwardVisible && <FastForward />}
        </Group>

        <Group>
          {isQualityVisible && (
            <Settings
              isQuality
              controlBarVisible={controlBarVisible}
              toggleProgressBar={onMenuToggle}
              displayProgressBarTimeoutRef={displayProgressBarTimeoutRef}
            />
          )}
          {!isMobile && (
            <Volume toggleProgressBar={onMenuToggle} displayProgressBarTimeoutRef={displayProgressBarTimeoutRef} />
          )}
          {!isPlayingAds && !hide.audio && (
            <Settings
              isAudio
              controlBarVisible={controlBarVisible}
              toggleProgressBar={onMenuToggle}
              displayProgressBarTimeoutRef={displayProgressBarTimeoutRef}
            />
          )}
          {!isPlayingAds && airplayEnabled && !hide.cast && <AirPlay />}
          {!hide.fullScreen && <Fullscreen />}
        </Group>
      </ControlsContainer>
    </ControlBarContainer>
  );
});

ControlBar.propTypes = {
  onHoverStateChange: PropTypes.func.isRequired,
};

export default React.memo(ControlBar);
